import React, { memo, useState, useCallback, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import { useTranslation } from 'react-i18next'
import Button from '@material-ui/core/Button'
import classNames from 'classnames'

import { UserAccountsRow, NewCompanyRoleUsers } from '.'
import styles from './styles'
import { Card, CardContent } from '@material-ui/core'
import { GetCompanyUsersRoles, StoreValue } from 'store'
import { EnumUserRoles } from 'utils'
import { EAppStateActionTypes } from 'models'
import {
	use_UserToCompanyRoleCollectionQuery,
	useHandleCompanyUsersRoleMutation,
	useUserQueryLazyQuery,
} from 'graphql/__generated__/graphql'

const useStyles = makeStyles(styles)

const UserAccountsView: React.FunctionComponent<any> = ({
	companyId = '',
	storeUserId,
	storeDispatch,
}) => {
	const classes = useStyles()
	const { t } = useTranslation()
	const [companyRolesIds, setCompanyRolesIds] = useState({
		[EnumUserRoles.COMPANY_ADMIN]: '',
		[EnumUserRoles.COMPANY_MANAGER]: '',
		[EnumUserRoles.JOB_PUBLISHER]: '',
		[EnumUserRoles.MODERATOR]: '',
		[EnumUserRoles.PUBLISHER]: '',
	})
	const [rolesAddForm, toggleRolesAddForm] = useState<{
		[key: string]: boolean
	}>({
		[EnumUserRoles.COMPANY_ADMIN]: false,
		[EnumUserRoles.COMPANY_MANAGER]: false,
		[EnumUserRoles.JOB_PUBLISHER]: false,
		[EnumUserRoles.MODERATOR]: false,
		[EnumUserRoles.PUBLISHER]: false,
	})

	const [companyRolesData, setCompanyRolesData] = useState<any>({
		[EnumUserRoles.COMPANY_ADMIN]: {
			initialValues: [], // initial values
			values: [], // value for async select
			id: '', // the id of the role
			users: [],
			title: 'Administrator',
			description: `Set up who has administrator rights at “company name”
Administrators are allowed to ..............`,
		},
		[EnumUserRoles.COMPANY_MANAGER]: {
			initialValues: [], // initial values
			values: [],
			id: '',
			users: [],
			title: 'Manager',
			description: `Set up who has administrator rights at “company name”
Administrators are allowed to ..............`,
		},
		[EnumUserRoles.JOB_PUBLISHER]: {
			initialValues: [], // initial values
			values: [],
			id: '',
			users: [],
			title: 'Job publisher',
			description: `Set up who has administrator rights at “company name”
Administrators are allowed to ..............`,
		},
		[EnumUserRoles.MODERATOR]: {
			initialValues: [], // initial values
			values: [],
			id: '',
			users: [],
			title: 'Moderator',
			description: `Set up who has administrator rights at “company name”
Administrators are allowed to ..............`,
		},
		[EnumUserRoles.PUBLISHER]: {
			initialValues: [], // initial values
			values: [],
			id: '',
			users: [],
			title: 'Publisher',
			description: `Set up who has administrator rights at “company name”
Administrators are allowed to ..............`,
		},
	})

	const { data: companyUsersRolesData } =
		use_UserToCompanyRoleCollectionQuery({
			variables: { companyId },
			fetchPolicy: 'cache-first',
		})
	const [getLoggedUser, { data: loggedUserData }] = useUserQueryLazyQuery({
		fetchPolicy: 'network-only',
	})

	const [handleUsersCompanyRoleMutation] = useHandleCompanyUsersRoleMutation({
		update(cache, { data }) {
			if (!data?.insertInto_UserToCompanyRoleCollection) return

			const query = GetCompanyUsersRoles

			try {
				cache.writeQuery({
					query,
					data: {
						getCompanyUsersRoles:
							data.insertInto_UserToCompanyRoleCollection,
					},
					variables: {
						companyId,
					},
				})
			} catch (e) {
				console.error(e)
			}
		},
	})

	const initiateState = useCallback(() => {
		if (companyUsersRolesData) {
			let adminUsers: any = []
			let managerUsers: any = []
			let jobPublisherUsers: any = []
			let moderatorUsers: any = []
			let publisherUsers: any = []

			let companyAdmins =
				(companyUsersRolesData._UserToCompanyRoleCollection &&
					companyUsersRolesData._UserToCompanyRoleCollection.edges.filter(
						(item: any) => {
							return item.role === EnumUserRoles.COMPANY_ADMIN
						},
					)) ??
				[]

			let companyManagers =
				companyUsersRolesData._UserToCompanyRoleCollection?.edges.filter(
					(item: any) => {
						return item.role === EnumUserRoles.COMPANY_MANAGER
					},
				) ?? []
			let companyJobPublishers =
				companyUsersRolesData._UserToCompanyRoleCollection?.edges.filter(
					(item: any) => {
						return item.role === EnumUserRoles.JOB_PUBLISHER
					},
				) ?? []
			let companyModerators =
				companyUsersRolesData._UserToCompanyRoleCollection?.edges.filter(
					(item: any) => {
						return item.role === EnumUserRoles.MODERATOR
					},
				) ?? []
			let companyPublisher =
				companyUsersRolesData._UserToCompanyRoleCollection?.edges.filter(
					(item: any) => {
						return item.role === EnumUserRoles.PUBLISHER
					},
				) ?? []

			// admin
			adminUsers = companyAdmins
				? companyAdmins?.length > 0
					? companyAdmins[0].node.user
					: []
				: []
			const adminValues = adminUsers?.map((item: any) => {
				return {
					value: item.id,
					label: `${item.profile.firstName} ${item.profile.lastName} `,
				}
			})
			const adminResult =
				adminUsers.length > 0 &&
				adminUsers?.map((item: any) => {
					return {
						user: item,
					}
				})

			const inheritedAdmins = adminUsers?.map((item: any) => {
				return {
					...item,
					inherited: true,
					[EnumUserRoles.COMPANY_ADMIN]: true,
				}
			})
			// manager
			managerUsers = companyManagers
				? companyManagers?.length > 0
					? companyManagers[0].node.user
					: []
				: []
			managerUsers = [...managerUsers, ...inheritedAdmins]

			const managerValues = managerUsers?.map((item: any) => {
				return {
					value: item.id,
					label: `${item.profile.firstName} ${item.profile.lastName} `,
				}
			})
			const managerResult =
				managerUsers.length > 0 &&
				managerUsers?.map((item: any) => {
					return {
						user: item,
					}
				})

			const inheritedManagers = managerUsers?.map((item: any) => {
				return {
					...item,
					inherited: true,
					[EnumUserRoles.COMPANY_MANAGER]: true,
				}
			})

			// job publisher
			jobPublisherUsers = companyJobPublishers
				? companyJobPublishers.length > 0
					? companyJobPublishers[0].node.user
					: []
				: []
			jobPublisherUsers = [...jobPublisherUsers, ...inheritedManagers]
			const jobPublisherValues = jobPublisherUsers?.map((item: any) => {
				return {
					value: item.id,
					label: `${item.profile.firstName} ${item.profile.lastName} `,
				}
			})
			const jobPublisherResult =
				jobPublisherUsers.length > 0 &&
				jobPublisherUsers?.map((item: any) => {
					return {
						user: { ...item },
					}
				})
			const inheritedJobPublishers = jobPublisherUsers?.map(
				(item: any) => {
					return {
						...item,
						inherited: true,
						[EnumUserRoles.COMPANY_MANAGER]: true,
					}
				},
			)

			// moderator
			moderatorUsers = companyModerators
				? companyModerators.length > 0
					? companyModerators[0].node.user
					: []
				: []
			moderatorUsers = [...moderatorUsers, ...inheritedJobPublishers]
			const moderatorValues = moderatorUsers?.map((item: any) => {
				return {
					value: item.id,
					label: `${item.profile.firstName} ${item.profile.lastName} `,
				}
			})

			const moderatorResult =
				moderatorUsers.length > 0 &&
				moderatorUsers?.map((item: any) => {
					return {
						user: { ...item, role: EnumUserRoles.MODERATOR },
					}
				})
			const inheritedModerators = moderatorUsers?.map((item: any) => {
				return {
					...item,
					inherited: true,
					[EnumUserRoles.MODERATOR]: true,
				}
			})

			// publisher
			publisherUsers = companyPublisher
				? companyPublisher.length > 0
					? companyPublisher[0].node.user
					: []
				: []
			publisherUsers = [...publisherUsers, ...inheritedModerators]
			const publisherValues = publisherUsers?.map((item: any) => {
				return {
					value: item.id,
					label: `${item.profile.firstName} ${item.profile.lastName} `,
				}
			})
			const publisherResult =
				publisherUsers.length > 0 &&
				publisherUsers?.map((item: any) => {
					return {
						user: { ...item, role: EnumUserRoles.PUBLISHER },
					}
				})

			setCompanyRolesIds({
				[EnumUserRoles.COMPANY_ADMIN]:
					(companyAdmins.length > 0 &&
						companyAdmins[0].node.user.id) ||
					'',
				[EnumUserRoles.COMPANY_MANAGER]:
					(companyManagers.length > 0 &&
						companyManagers[0].node.user.id) ||
					'',
				[EnumUserRoles.JOB_PUBLISHER]:
					(companyJobPublishers.length > 0 &&
						companyJobPublishers[0].node.user.id) ||
					'',
				[EnumUserRoles.MODERATOR]:
					(companyModerators.length > 0 &&
						companyModerators[0].node.user.id) ||
					'',
				[EnumUserRoles.PUBLISHER]:
					(companyPublisher.length > 0 &&
						companyPublisher[0].node.user.id) ||
					'',
			})

			setCompanyRolesData((prevState: any) => {
				prevState.COMPANY_ADMIN = {
					...prevState.COMPANY_ADMIN,
					id:
						companyAdmins.length > 0 &&
						companyAdmins[0].node.user.id,
					initialValues: adminValues,
					values: [],
					users: adminResult,
				}

				prevState.COMPANY_MANAGER = {
					...prevState.COMPANY_MANAGER,
					id:
						companyManagers.length > 0 &&
						companyManagers[0].node.user.id,
					initialValues: managerValues,
					values: [],
					users: managerResult,
				}

				prevState.JOB_PUBLISHER = {
					...prevState.JOB_PUBLISHER,
					id:
						companyJobPublishers.length > 0 &&
						companyJobPublishers[0].node.user.id,
					initialValues: jobPublisherValues,
					values: [],
					users: jobPublisherResult,
				}

				prevState.MODERATOR = {
					...prevState.MODERATOR,
					id:
						companyModerators.length > 0 &&
						companyModerators[0].node.user.id,
					initialValues: moderatorValues,
					values: [],
					users: moderatorResult,
				}

				prevState.PUBLISHER = {
					...prevState.PUBLISHER,
					id:
						companyPublisher.length > 0 &&
						companyPublisher[0].node.user.id,
					initialValues: publisherValues,
					values: [],
					users: publisherResult,
				}
				return { ...prevState }
			})
		}
	}, [companyUsersRolesData])

	const handleChange = useCallback(
		(inputVal: any, roleType: EnumUserRoles) => {
			let value = inputVal ? inputVal : []
			console.debug(
				'%c DataDebug',
				'color:#fc03c2; font-style: italic; font-size:16px;',
				{ inputVal, roleType },
			)

			switch (roleType) {
				case EnumUserRoles.COMPANY_ADMIN:
					setCompanyRolesData((prevState: any) => {
						prevState.COMPANY_ADMIN = {
							...prevState.COMPANY_ADMIN,
							values: value,
						}
						return { ...prevState }
					})
					break

				case EnumUserRoles.COMPANY_MANAGER:
					setCompanyRolesData((prevState: any) => {
						prevState.COMPANY_MANAGER = {
							...prevState.COMPANY_MANAGER,
							values: value,
						}
						return { ...prevState }
					})
					break

				case EnumUserRoles.JOB_PUBLISHER:
					setCompanyRolesData((prevState: any) => {
						prevState.JOB_PUBLISHER = {
							...prevState.JOB_PUBLISHER,
							values: value,
						}
						return { ...prevState }
					})
					break

				case EnumUserRoles.MODERATOR:
					setCompanyRolesData((prevState: any) => {
						prevState.MODERATOR = {
							...prevState.MODERATOR,
							values: value,
						}
						return { ...prevState }
					})
					break

				case EnumUserRoles.PUBLISHER:
					setCompanyRolesData((prevState: any) => {
						prevState.PUBLISHER = {
							...prevState.PUBLISHER,
							values: value,
						}
						return { ...prevState }
					})
					break

				default:
					break
			}
		},
		[],
	)

	const handleCompanyRole = useCallback(
		(roleType: any, actionType: 'SAVE' | 'CANCEL' | 'OPEN') => {
			// Save
			if (actionType === 'CANCEL' || actionType === 'OPEN') {
				toggleRolesAddForm({
					// close the previous form
					[EnumUserRoles.COMPANY_ADMIN]: false,
					[EnumUserRoles.COMPANY_MANAGER]: false,
					[EnumUserRoles.JOB_PUBLISHER]: false,
					[EnumUserRoles.MODERATOR]: false,
					[EnumUserRoles.PUBLISHER]: false,
					// @ts-ignore TODO: come back and remove this ignore.
					[EnumUserRoles[roleType]]: !rolesAddForm[roleType],
				})
				initiateState()
				return
			}
			if (actionType === 'SAVE') {
				let data: {
					id: string
					usersId: string[]
					role: string
				} = { id: '', usersId: [], role: '' }
				switch (roleType) {
					case EnumUserRoles.COMPANY_ADMIN:
						let adminValuesIds = companyRolesData.COMPANY_ADMIN
							.values
							? companyRolesData.COMPANY_ADMIN.values?.map(
									(item: any) => item.value,
							  )
							: []

						data = {
							id: companyRolesData.COMPANY_ADMIN.id
								? companyRolesData.COMPANY_ADMIN.id
								: '',
							usersId: adminValuesIds,
							role: EnumUserRoles.COMPANY_ADMIN,
						}
						toggleRolesAddForm({
							...rolesAddForm,
							[EnumUserRoles.COMPANY_ADMIN]:
								!rolesAddForm[EnumUserRoles.COMPANY_ADMIN],
						})
						break
					case EnumUserRoles.COMPANY_MANAGER:
						let managerValuesIds = companyRolesData.COMPANY_MANAGER
							.values
							? companyRolesData.COMPANY_MANAGER.values?.map(
									(item: any) => item.value,
							  )
							: []
						data = {
							id: companyRolesData.COMPANY_MANAGER.id
								? companyRolesData.COMPANY_MANAGER.id
								: '',
							usersId: managerValuesIds,
							role: EnumUserRoles.COMPANY_MANAGER,
						}
						toggleRolesAddForm({
							...rolesAddForm,
							[EnumUserRoles.COMPANY_MANAGER]:
								!rolesAddForm[EnumUserRoles.COMPANY_MANAGER],
						})
						break
					case 'JOB_PUBLISHER':
						let jobPublisherValuesIds = companyRolesData
							.JOB_PUBLISHER.values
							? companyRolesData.JOB_PUBLISHER.values?.map(
									(item: any) => item.value,
							  )
							: []
						data = {
							id: companyRolesData.JOB_PUBLISHER.id
								? companyRolesData.JOB_PUBLISHER.id
								: '',
							usersId: jobPublisherValuesIds,
							role: EnumUserRoles.JOB_PUBLISHER,
						}
						toggleRolesAddForm({
							...rolesAddForm,
							[EnumUserRoles.JOB_PUBLISHER]:
								!rolesAddForm[EnumUserRoles.JOB_PUBLISHER],
						})
						break
					case 'MODERATOR':
						let moderatorValuesIds = companyRolesData.MODERATOR
							.values
							? companyRolesData.MODERATOR.values?.map(
									(item: any) => item.value,
							  )
							: []

						data = {
							id: companyRolesData.MODERATOR.id
								? companyRolesData.MODERATOR.id
								: '',
							usersId: moderatorValuesIds,
							role: EnumUserRoles.MODERATOR,
						}
						toggleRolesAddForm({
							...rolesAddForm,
							[EnumUserRoles.MODERATOR]:
								!rolesAddForm[EnumUserRoles.MODERATOR],
						})

						break

					case 'PUBLISHER':
						let publisherValuesIds = companyRolesData.PUBLISHER
							.values
							? companyRolesData.PUBLISHER.values?.map(
									(item: any) => item.value,
							  )
							: []

						data = {
							id: companyRolesData.PUBLISHER.id
								? companyRolesData.PUBLISHER.id
								: '',
							usersId: publisherValuesIds,
							role: EnumUserRoles.PUBLISHER,
						}
						toggleRolesAddForm({
							...rolesAddForm,
							[EnumUserRoles.PUBLISHER]:
								!rolesAddForm[EnumUserRoles.PUBLISHER],
						})

						break

					default:
						break
				}
				handleUsersCompanyRoleMutation({
					variables: {
						data: {
							...data,
							B: companyId,
							// companyId: {companyId},
						},
					},
				}).then(() => {
					const checkLoggedUser = data.usersId.includes(storeUserId)
					if (checkLoggedUser) {
						getLoggedUser({
							variables: {
								id: storeUserId,
							},
						})
					}
				})
			}
		},
		[
			handleUsersCompanyRoleMutation,
			companyRolesData,
			companyId,
			rolesAddForm,
			initiateState,
			getLoggedUser,
			storeUserId,
		],
	)

	useEffect(() => {
		initiateState()
	}, [companyUsersRolesData, initiateState])

	useEffect(() => {
		if (loggedUserData) {
			storeDispatch({
				type: EAppStateActionTypes.updateUser,
				user: loggedUserData.userCollection?.edges,
			})
		}
	}, [loggedUserData, storeDispatch])

	return (
		<Grid
			className={classes.userAccountsRoot}
			container={true}
			direction="column"
			spacing={2}
		>
			{Object.keys(companyRolesData)?.map((item, index: number) => {
				return (
					<Grid item key={`${index}_grid`}>
						<Card
							key={`${item}_${index}_card`}
							className={classes.cardRoot}
							elevation={0}
						>
							<CardContent key={`${item}_${index}_card_content`}>
								<div className={classes.titleSection}>
									<span className={classes.title}>
										{t(`${companyRolesData[item].title}`)}
									</span>
									<div>
										{!rolesAddForm[item] ? (
											<Button
												className={classNames(
													classes.actionTitle,
													{
														[classes.actionTitleMargin]:
															rolesAddForm[item],
													},
												)}
												onClick={() =>
													handleCompanyRole(
														//@ts-ignore
														EnumUserRoles[item],
														'OPEN',
													)
												}
											>
												{t(`${t('Add')}`)}
											</Button>
										) : (
											<Button
												disabled={
													companyRolesData[item]
														.values &&
													companyRolesData[item]
														.values.length === 0
												}
												className={classNames(
													classes.actionTitle,
													{
														[classes.actionTitleMargin]:
															rolesAddForm[item],
													},
												)}
												onClick={() =>
													handleCompanyRole(
														//@ts-ignore
														EnumUserRoles[item],
														'SAVE',
													)
												}
											>
												{t(`${t('Save')}`)}
											</Button>
										)}
										{rolesAddForm[item] && (
											<Button
												className={classes.actionTitle}
												onClick={() =>
													handleCompanyRole(
														//@ts-ignore
														EnumUserRoles[item],
														'CANCEL',
													)
												}
											>
												{t('Cancel')}
											</Button>
										)}
									</div>
								</div>
								{rolesAddForm[item] && (
									<NewCompanyRoleUsers
										handleChange={handleChange}
										usersValues={
											companyRolesData[item].initialValues
										}
										companyRoleType={item}
									/>
								)}
								<div className={classes.cardDescription}>
									<span>
										{companyRolesData[item].description}
									</span>
								</div>
								<Grid
									key={`${item}_${index}_row`}
									container
									direction={'column'}
									spacing={1}
								>
									{companyRolesData[item].users &&
										companyRolesData[item]?.users?.length >
											0 &&
										companyRolesData[item]?.users?.map(
											(elem: any, index: number) => {
												return (
													<UserAccountsRow
														companyRoleId={
															companyRolesData[
																item
															].id
														}
														key={`${elem.id}_${index}`}
														item={elem.user}
														companyId={companyId}
														roleType={item}
														companyRolesIds={
															companyRolesIds
														}
													/>
												)
											},
										)}
								</Grid>
							</CardContent>
						</Card>
					</Grid>
				)
			})}
		</Grid>
	)
}

const UserAccounts = memo((props: any) => (
	<StoreValue keys={['user.id|storeUserId', 'storeDispatch']}>
		<UserAccountsView {...props} />
	</StoreValue>
))

export default memo(UserAccounts)
